import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import { adminGetTechQuizAttempts } from '../../api/Api';
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { TECH_QUIZZES } from '../TechQuiz/registry';

interface PropsAdminTechQuizzes {
    token: string;
}

interface Attempt {
    id: string;
    user_id: string | null;
    username: string | null;
    quiz_slug: string;
    started_at: string | null;
    finished_at: string | null;
    completed: boolean;
    timed_out: boolean;
    duration_seconds: number | null;
    score: number | null;
    score_easy: number | null;
    score_medium: number | null;
    score_hard: number | null;
    total_questions: number;
}

interface Stats {
    started: number;
    completed: number;
    completion_rate: number;
    avg_score: number | null;
    avg_duration_seconds: number | null;
}

const PER_PAGE = 50;

const fmtDate = (iso: string | null) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
};

const fmtDuration = (secs: number | null) => {
    if (secs == null) return '-';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const AdminTechQuizzes = ({ token }: PropsAdminTechQuizzes) => {
    const colors = useColors();
    const [slug, setSlug] = useState(TECH_QUIZZES[0].slug);
    const [attempts, setAttempts] = useState<Attempt[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = useCallback(
        async (s: string, p: number) => {
            setLoading(true);
            const data = await adminGetTechQuizAttempts(token, s, p, PER_PAGE);
            if (data) {
                setAttempts(data.attempts || []);
                setStats(data.stats || null);
                setTotal(data.total || 0);
                setPage(data.page || p);
            } else {
                setAttempts([]);
                setStats(null);
                setTotal(0);
            }
            setLoading(false);
        },
        [token],
    );

    useEffect(() => {
        load(slug, 1);
    }, [load, slug]);

    const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));
    const activeQuiz = TECH_QUIZZES.find((q) => q.slug === slug);

    const statCards: { label: string; value: string }[] = stats
        ? [
              { label: 'Started', value: String(stats.started) },
              { label: 'Completed', value: String(stats.completed) },
              { label: 'Completion', value: `${Math.round(stats.completion_rate * 100)}%` },
              { label: 'Avg score', value: stats.avg_score != null ? `${stats.avg_score.toFixed(1)} / 30` : '-' },
              { label: 'Avg time', value: fmtDuration(stats.avg_duration_seconds) },
          ]
        : [];

    return (
        <PageContainer>
            <CyberpunkTitle title="Tech Quizzes" subtitle={`${activeQuiz?.topic.toUpperCase()} · ${total} ATTEMPTS`} mb="24px" />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 3 }}>
                {TECH_QUIZZES.map((q) => {
                    const selected = q.slug === slug;
                    return (
                        <Button
                            key={q.slug}
                            size="small"
                            variant={selected ? 'contained' : 'outlined'}
                            onClick={() => setSlug(q.slug)}
                            sx={{
                                fontFamily: '"Share Tech Mono", monospace',
                                fontSize: '0.72rem',
                                letterSpacing: '0.06em',
                                textTransform: 'none',
                                ...(selected ? {} : { borderColor: colors.border.default, color: colors.text.secondary }),
                            }}
                        >
                            {q.topic}
                        </Button>
                    );
                })}
            </Box>

            {stats && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' },
                        gap: '12px',
                        mb: 3,
                    }}
                >
                    {statCards.map((c) => (
                        <GlowCard key={c.label} glowOnHover={false}>
                            <Box sx={{ p: '16px 18px' }}>
                                <Typography
                                    sx={{
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.66rem',
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                        color: colors.accent.cyan,
                                        mb: 0.5,
                                    }}
                                >
                                    {c.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: '"Orbitron", sans-serif',
                                        fontWeight: 700,
                                        fontSize: '1.35rem',
                                        color: colors.text.primary,
                                    }}
                                >
                                    {c.value}
                                </Typography>
                            </Box>
                        </GlowCard>
                    ))}
                </Box>
            )}

            <GlowCard glowOnHover={false}>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow
                                sx={{
                                    borderBottom: `1px solid ${colors.border.accent}`,
                                    '& th': {
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                        color: colors.accent.cyan,
                                        borderBottom: 'none',
                                        padding: '14px 16px',
                                        backgroundColor: 'transparent',
                                        whiteSpace: 'nowrap',
                                    },
                                }}
                            >
                                <TableCell>#</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Score</TableCell>
                                <TableCell align="center">E / M / H</TableCell>
                                <TableCell align="center">Time</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attempts.map((a, idx) => (
                                <TableRow
                                    key={a.id}
                                    sx={{
                                        '&:last-child td': { border: 0 },
                                        borderBottom: `1px solid ${colors.border.subtle}`,
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': { backgroundColor: 'rgba(185, 28, 28, 0.04)' },
                                        '& td': { borderBottom: 'none', padding: '12px 16px', whiteSpace: 'nowrap' },
                                    }}
                                >
                                    <TableCell>
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.75rem', color: colors.text.muted }}>
                                            {String((page - 1) * PER_PAGE + idx + 1).padStart(3, '0')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.78rem', color: colors.text.secondary }}>
                                            {fmtDate(a.started_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {a.username ? (
                                            <Typography
                                                component={RouterLink}
                                                to={`/profile/${a.username}`}
                                                title={a.user_id || undefined}
                                                sx={{
                                                    fontFamily: '"Orbitron", sans-serif',
                                                    fontWeight: 600,
                                                    fontSize: '0.82rem',
                                                    color: colors.accent.cyan,
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                            >
                                                {a.username}
                                            </Typography>
                                        ) : a.user_id ? (
                                            <Typography
                                                title={a.user_id}
                                                sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.72rem', color: colors.text.muted }}
                                            >
                                                {a.user_id.slice(0, 8)}... <Box component="span" sx={{ opacity: 0.7 }}>(deleted)</Box>
                                            </Typography>
                                        ) : (
                                            <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.72rem', color: colors.text.muted }}>
                                                anonymous
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {a.completed ? (
                                            <Chip
                                                label={a.timed_out ? 'Timed out' : 'Completed'}
                                                size="small"
                                                sx={{
                                                    fontFamily: '"Share Tech Mono", monospace',
                                                    fontSize: '0.62rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.08em',
                                                    textTransform: 'uppercase',
                                                    height: '22px',
                                                    backgroundColor: a.timed_out ? colors.accent.redGlow : colors.accent.greenGlow,
                                                    color: a.timed_out ? colors.accent.red : colors.accent.green,
                                                    border: `1px solid ${(a.timed_out ? colors.accent.red : colors.accent.green)}55`,
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label="In progress"
                                                size="small"
                                                sx={{
                                                    fontFamily: '"Share Tech Mono", monospace',
                                                    fontSize: '0.62rem',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.08em',
                                                    textTransform: 'uppercase',
                                                    height: '22px',
                                                    backgroundColor: 'transparent',
                                                    color: colors.text.muted,
                                                    border: `1px solid ${colors.border.subtle}`,
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography sx={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 700, color: colors.text.primary }}>
                                            {a.score != null ? `${a.score} / ${a.total_questions}` : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.78rem', color: colors.text.secondary }}>
                                            {a.completed
                                                ? `${a.score_easy ?? '-'} / ${a.score_medium ?? '-'} / ${a.score_hard ?? '-'}`
                                                : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.78rem', color: colors.text.secondary }}>
                                            {fmtDuration(a.duration_seconds)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {loading && (
                    <Box sx={{ textAlign: 'center', padding: '60px 0' }}>
                        <CircularProgress size={40} sx={{ color: colors.accent.cyan }} />
                    </Box>
                )}

                {!loading && !attempts.length && (
                    <Box sx={{ textAlign: 'center', padding: '48px 0' }}>
                        <Typography sx={{ color: colors.text.muted }}>No attempts recorded yet for this quiz.</Typography>
                    </Box>
                )}

                {!loading && total > PER_PAGE && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '18px 0' }}>
                        <Button size="small" variant="text" disabled={page <= 1} onClick={() => load(slug, page - 1)}>
                            Previous
                        </Button>
                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.78rem', color: colors.text.muted }}>
                            Page {page} / {pageCount}
                        </Typography>
                        <Button size="small" variant="text" disabled={page >= pageCount} onClick={() => load(slug, page + 1)}>
                            Next
                        </Button>
                    </Box>
                )}
            </GlowCard>
        </PageContainer>
    );
};

export default AdminTechQuizzes;
